import type {
  AccountPreference,
  AccountsPreferences,
  AccountsPreferencesValue,
  PreferenceDocument,
} from './preferencesTypes';

type AccountPreferencesByIdDraft = { [accountId: string]: AccountPreference | undefined };

// Accounts as an array or an Immutable collection of accounts.
type Accounts = {
  forEach(sideEffect: (account: { readonly id?: string | null }) => unknown): unknown;
};

export const isPreferenceDocument = (value: unknown): value is PreferenceDocument =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const colorOptions: readonly string[] = [
  '#FB7F79',
  '#A5A6A9',
  '#FC6D30',
  '#1793D2',
  '#FDA573',
  '#FEE38B',
  '#49B750',
  '#ADD7AD',
  '#474F57',
  '#1CB3C1',
  '#9478B9',
  '#84DAE0',
  '#3F5CAC',
  '#96A6D2',
  '#5B2C93',
  '#AA8FB6',
  '#75C2E7',
  '#2B2E34',
  '#F62F3C',
  '#A2A8AD',
  '#622770',
  '#81888E',
  '#FED449',
  '#AFB4B9',
];

export function colorLookup(index: number | undefined): string | undefined {
  return index === undefined ? undefined : colorOptions[index];
}

function getNextUnusedColor(prefsById: AccountPreferencesByIdDraft): number {
  let maxVal = 0;
  Object.values(prefsById).forEach((item) => {
    maxVal = Math.max(item?.color ? item.color : 0, maxVal);
  });
  return (maxVal + 1) % colorOptions.length; // rotate through colors
}

/** Returns the account preferences with a color for every account, or null when none was missing. */
export function initAccountPrefs(
  accounts: Accounts,
  accountPrefs: AccountsPreferencesValue | null | undefined,
): AccountsPreferences | null {
  // ALERT
  // Until prefs allows us to delete a preference, I am marking this preference deleted with
  // the string 'clear'
  //
  const current: AccountsPreferences = accountPrefs && accountPrefs !== 'clear' ? accountPrefs : { byId: {} };
  if (!isPreferenceDocument(current.byId)) {
    return null;
  }

  const prefsById: AccountPreferencesByIdDraft = { ...current.byId };
  let dirty = false;
  accounts.forEach(({ id }) => {
    const pref = id ? prefsById[id] : undefined;
    if (id && pref?.color == null) {
      prefsById[id] = { ...pref, color: getNextUnusedColor(prefsById) };
      dirty = true;
    }
  });

  return dirty ? { ...current, byId: prefsById } : null;
}
